import DsPlayerScouting from "@/components/DsScouting/Player";
import {setPlayerTab} from "@/actions";
import {useEffect, useState} from "react";
import moment from "moment";
import {useSelector, useDispatch} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";

import Page from "@/components/Page";
import PlayerAvatar from "@/components/PlayerAvatar";
import {
	DsChips,
	DsEmpty,
	DsKpi,
	DsKpiRow,
	DsLink,
	DsMissing,
	DsSection,
	DsSkeleton,
	DsStatus,
	DsTable,
} from "@/components/DsKit";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {ContractAPI} from "@/api/contract";
import {GpsAPI} from "@/api/gps";
import {PlayerAPI} from "@/api/player";
import {PlayerValuationAPI} from "@/api/player_valuation";
import {ScoutingReportAPI} from "@/api/scouting_report";
import {useClubSnapshot} from "@/utils/hooks";
import {
	formatEuro,
	playerName,
	summarizeDsGps,
	summarizeDsMedicalDays,
} from "@/utils/clubSnapshot";
import {
	loadPlayerRealStats,
	playerSeasonSummaryCards,
} from "@/utils/computePlayerStats";
import {computePlayerSummary} from "@/utils/playerIndices";
import {ERROR_OPERATION} from "@/utils/constants";

// Sintesi del giocatore: ogni tab della scheda completa ridotta a ciò che serve
// al DS (Informazioni, Statistiche, Performance, Antropometria, Valutazioni, Medica).

const CONTRACT_TYPES = {
	professional: "Professionistico",
	amateur: "Dilettantistico",
	youth: "Settore giovanile",
	loan: "Prestito",
	amateur_professional: "Semiprofessionistico",
};

const CONTRACT_STATUS = {
	active: "Attivo secondo il contratto",
	expired: "Scaduto",
	terminated: "Risolto",
	suspended: "Sospeso",
};

const INDEX_TONE = {green: "green", yellow: "yellow", red: "red"};

const fmt = (v, decimals = 0) =>
	v === null || v === undefined || isNaN(v)
		? "—"
		: Number(v).toLocaleString("it-IT", {maximumFractionDigits: decimals});

const noteText = (n) => n?.notes || n?.note || n?.text || n?.description || "";
const latestBy = (rows, field) =>
	[...(rows || [])].sort((a, b) => moment(b[field]) - moment(a[field]))[0];
const growthDate = (g) => g.date || g.creationDate;

function DsPlayerGps({playerId, seasonId}) {
	const [state, setState] = useState({loading: true, rows: [], error: false});
	const [reload, setReload] = useState(0);
	useEffect(() => {
		let active = true;
		setState({loading: true, rows: [], error: false});
		GpsAPI.playerList(playerId, {seasonId}).then(
			(rows) => {
				if (active) setState({loading: false, rows: rows || [], error: false});
			},
			() => {
				if (active) setState({loading: false, rows: [], error: true});
			},
		);
		return () => {
			active = false;
		};
	}, [playerId, seasonId, reload]);
	if (state.loading) return <p role="status">Caricamento dati GPS…</p>;
	if (state.error)
		return (
			<p role="alert">
				Impossibile caricare i dati GPS delle partite.{" "}
				<button
					className="ds-btn"
					aria-label="Riprova caricamento GPS"
					onClick={() => setReload((n) => n + 1)}
				>
					Riprova
				</button>
			</p>
		);
	if (!state.rows.length)
		return <p>Nessun dato GPS nelle partite di questa stagione.</p>;
	const summary = summarizeDsGps(state.rows);
	return (
		<>
			<p className="ds-muted">
				{summary.games} gare con rilevazioni · copertura indicata per metrica.
				Media tra gare; picco massimo per metrica.
			</p>
			<DsTable
				columns={[
					{key: "label", label: "Sintesi"},
					...summary.metrics.map((metric) => ({
						key: metric.key,
						label: `${metric.label}${metric.unit ? " (" + metric.unit + ")" : ""}`,
						render: (row) => (
							<span>
								{fmt(metric[row.id], 2)}{" "}
								<small className="ds-muted">
									({metric.known}/{metric.total})
								</small>
							</span>
						),
					})),
				]}
				rows={[
					{id: "mean", label: "Media"},
					{id: "peak", label: "Picco"},
				]}
			/>
			{(summary.excludedGames > 0 || summary.unidentified > 0) && (
				<p role="status">
					{summary.excludedGames} gare escluse: tempi incompleti o duplicati.{" "}
					{summary.unidentified > 0
						? `${summary.unidentified} sessioni senza gara escluse.`
						: ""}
				</p>
			)}
			<p className="ds-muted">
				Prima il totale della gara; altrimenti somma dei due tempi per distanze
				e conteggi, massimo per velocità e accelerazione massima. Rapporti,
				percentuali e altre misure richiedono il record intera gara. Valori
				mancanti esclusi, zero conservato.
			</p>
		</>
	);
}

function DsPlayerPage(props) {
	const {handleNotification} = props;
	const {id} = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const season = useSelector((state) => state?.season);
	const seasonId = season?.id;
	const {
		data: s,
		loading: snapshotLoading,
		error: snapshotError,
	} = useClubSnapshot({handleNotification});
	const full = s?.full || s;

	const [detail, setDetail] = useState(null);
	const [detailLoading, setDetailLoading] = useState(true);

	useEffect(() => {
		if (!id || !seasonId) return;
		let active = true;
		const load = async () => {
			setDetailLoading(true);
			try {
				const player = await PlayerAPI.getById(id, {seasonId});
				if (!active) return;
				const [realStats, valuations, reports, notes, contracts] =
					await Promise.all([
						loadPlayerRealStats(player, seasonId),
						PlayerValuationAPI.playerList(id, {seasonId}),
						ScoutingReportAPI.getByPlayerId(id, {seasonId}),
						PlayerAPI.getNotes(id),
						ContractAPI.listByPlayer(id).then(
							(rows) => ({rows: rows || [], available: true}),
							() => ({rows: [], available: false}),
						),
					]);
				if (realStats?.error) throw new Error(realStats.error);
				if (active)
					setDetail({
						player,
						realStats,
						valuations: valuations || [],
						reports: reports || [],
						notes: notes || [],
						contracts: contracts.rows,
						contractsAvailable: contracts.available,
					});
			} catch {
				if (active) {
					setDetail(null);
					handleNotification(ERROR_OPERATION, "error");
				}
			} finally {
				if (active) setDetailLoading(false);
			}
		};
		load();
		return () => {
			active = false;
		};
	}, [id, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	const memberships = full?.teamsByPlayer?.get(id) || [];
	const [membershipId, setMembershipId] = useState(null);
	useEffect(() => {
		setMembershipId(null);
	}, [id, seasonId, full?.context, s?.teamScope]);
	const team =
		memberships.find((entry) => entry.id === (membershipId || s?.teamScope)) ||
		(memberships.length === 1 ? memberships[0] : null);
	const player = detail?.player || null;
	const medicalDays = summarizeDsMedicalDays(full, [id]);
	const injury = full?.activeInjuries.find((i) => i.playerId === id);
	const injuries = (full?.seasonInjuries || []).filter(
		(i) => i.playerId === id,
	);
	const wellness = team?.wellness.players.find((w) => w.player.id === id);
	const trainingAttendance = team?.organization.attendance.rows?.find(
		(row) => row.playerId === id,
	);
	const stats = detail?.realStats?.outfield;
	const age = player?.dateOfBirth
		? moment().diff(moment(player.dateOfBirth), "years")
		: null;

	const contracts = detail?.contracts || [];
	const agent = latestBy(player?.playersAgentsHistory, "dateFrom");
	const agentName = agent?.name;
	const career = [...(player?.career || [])]
		.sort((a, b) => (b.season?.name || "").localeCompare(a.season?.name || ""))
		.slice(0, 3);

	// Valutazioni, report e rating
	const valuations = [...(detail?.valuations || [])].sort(
		(a, b) =>
			moment(b.valuationDate || b.creationDate) -
			moment(a.valuationDate || a.creationDate),
	);
	const ratings = valuations
		.map((v) => v.valuationRating)
		.filter((r) => r !== null && r !== undefined);
	const playerRating = ratings.length
		? ratings.reduce((n, r) => n + Number(r), 0) / ratings.length
		: null;
	const reports = [...(detail?.reports || [])].sort(
		(a, b) =>
			moment(b.reportDate || b.creationDate) -
			moment(a.reportDate || a.creationDate),
	);
	const notes = [...(detail?.notes || [])]
		.filter((n) => noteText(n))
		.sort((a, b) => moment(b.creationDate) - moment(a.creationDate));

	// Fisico e performance
	const growth = (player?.playersGrowth || [])
		.filter((g) => g.height)
		.sort((a, b) => moment(growthDate(a)) - moment(growthDate(b)));
	const growthYears =
		growth.length > 1
			? moment(growthDate(growth[growth.length - 1])).diff(
					moment(growthDate(growth[0])),
					"years",
					true,
				)
			: 0;
	const growthRate =
		growthYears >= 0.25
			? (growth[growth.length - 1].height - growth[0].height) / growthYears
			: null;
	const lastTest = latestBy(player?.athleticTests, "testDate");

	const indices =
		player && detail
			? computePlayerSummary({
					playerData: player,
					avgTQR: wellness?.avgTQR ?? null,
					avgRPE: wellness?.avgRPE ?? null,
					isInjured: !!injury,
					injuries,
					valuations,
					realStats: detail.realStats,
					seasonStart: season?.startDate,
					seasonEnd: season?.endDate,
				})
			: null;

	const missing = [
		stats?.matchesPlayed == null && {
			label: "Presenze e statistiche gara",
			owner: "Staff tecnico",
		},
		team &&
			wellness?.avgTQR == null && {
				label: "Recupero e fatica",
				owner: "Giocatore / performance",
			},
		!valuations.length && {label: "Valutazioni", owner: "Staff tecnico"},
		!lastTest && {label: "Test atletici", owner: "Preparatore"},
		detail?.contractsAvailable &&
			!contracts.length && {label: "Contratto", owner: "Direzione"},
	].filter(Boolean);

	const ready = s && !detailLoading;

	return (
		<Page>
			<div className="ds ds-player">
				{snapshotError && (
					<DsEmpty>Dati DS non disponibili o accesso non consentito.</DsEmpty>
				)}
				<DsSkeleton visible={snapshotLoading || detailLoading} />
				{ready && !player && (
					<DsEmpty>
						Dati giocatore non disponibili o accesso non consentito.
					</DsEmpty>
				)}
				{ready && player && (
					<>
						<div className="ds-profile">
							<PlayerAvatar
								firstName={player.firstName}
								lastName={player.lastName}
								imageUrl={player.imageUrl}
								size="large"
								status={injury ? "critical" : undefined}
								statusLabel={injury ? "Infortunato" : undefined}
							/>
							<div className="ds-profile__main">
								<h1 className="ds-profile__name">{playerName(player)}</h1>
								<div className="ds-profile__sub">
									{[
										player.goalkeeper ? "Portiere" : player.primaryRole,
										player.secondaryRole,
										age !== null && `${age} anni`,
										player.favoriteFoot &&
											`piede ${player.favoriteFoot.toLowerCase()}`,
										player.nationality,
										memberships.map((entry) => entry.name).join(" · "),
									]
										.filter(Boolean)
										.join(" · ")}
								</div>
								<div className="ds-pills">
									{injury ? (
										<DsStatus
											status="alert"
											label={`Infortunato · ${injury.episodes?.length ?? 1} episodi attivi`}
										/>
									) : (
										<DsStatus
											status="none"
											label={
												full?.medicalPlayerIds?.has(id)
													? "Senza infortunio attivo in cartella"
													: "Nessuna cartella consultata"
											}
										/>
									)}
									<DsStatus
										status="none"
										label={
											detail.contractsAvailable
												? `${contracts.length} contratti inseriti`
												: "Contratti non disponibili"
										}
									/>
									{agentName && (
										<DsStatus
											status="none"
											label={`Procuratore: ${agentName}`}
										/>
									)}
								</div>
							</div>
							<div className="ds-profile__actions">
								<button
									className="ds-btn ds-btn--ghost"
									onClick={() => navigate("/players/" + id)}
								>
									<OpenInNewIcon fontSize="small" /> Scheda completa
								</button>
							</div>
						</div>

						{memberships.length > 1 && (
							<div
								className="ds-toolbar"
								role="group"
								aria-label="Rosa del giocatore"
							>
								<span className="ds-muted">Rosa per presenze e benessere</span>
								<DsChips
									options={memberships.map((entry) => ({
										value: entry.id,
										label: entry.name,
									}))}
									value={team?.id || ""}
									onChange={setMembershipId}
								/>
							</div>
						)}
						{memberships.length > 1 && !team && (
							<DsEmpty>
								Scegli una rosa per presenze e benessere. Le statistiche
								stagionali gara comprendono tutte le appartenenze autorizzate.
							</DsEmpty>
						)}
						{memberships.length === 0 && (
							<DsEmpty>
								Nessuna appartenenza nelle rose autorizzate della stagione.
							</DsEmpty>
						)}
						<DsMissing items={missing} />

						<DsKpiRow>
							<DsKpi
								label="Prontezza"
								value={indices?.readiness?.value ?? "—"}
								sub={
									"Indice sperimentale DS · " +
									(indices?.readiness?.label || "dati insufficienti")
								}
								tone={INDEX_TONE[indices?.readiness?.color]}
							/>
							<DsKpi
								label="Rendimento"
								value={indices?.performance?.value ?? "—"}
								sub={
									"Indice sperimentale DS · " +
									(indices?.performance?.value == null
										? "dati tecnici insufficienti"
										: indices.performance.label)
								}
								tone={INDEX_TONE[indices?.performance?.color]}
							/>
							<DsKpi
								label="Disponibilità stagionale"
								value={
									medicalDays.availability === null
										? "—"
										: fmt(medicalDays.availability, 1) + "%"
								}
								sub={`${medicalDays.days ?? "—"} giorni persi / ${medicalDays.elapsed} giorni trascorsi · cartella ${medicalDays.known}/1`}
							/>
							<DsKpi
								label="Valutazione staff"
								value={fmt(playerRating, 1)}
								sub={
									valuations.length
										? `media di ${valuations.length} valutazioni`
										: "nessuna valutazione"
								}
							/>
						</DsKpiRow>

						<DsSection
							title="Statistiche gara"
							hint={`Stagione ${season?.name || "selezionata"} · tutte le rose e competizioni autorizzate`}
						>
							<DsKpiRow>
								{playerSeasonSummaryCards(detail.realStats).map((card) => (
									<DsKpi
										key={card.key}
										label={card.label}
										value={card.value}
										sub={card.coverage}
									/>
								))}
							</DsKpiRow>
							<p className="ds-muted">
								Totali sui dati inseriti nelle gare giocate. «—» indica un dato
								mancante o in conflitto; 0 è un valore inserito.
							</p>
						</DsSection>

						<div className="ds-two-cols">
							<DsSection title="Disponibilità e condizione">
								<div className="ds-facts">
									<div className="ds-facts__row">
										<span>Stato</span>
										<b>
											{injury
												? (injury.episodes || [injury])
														.map(
															(episode) =>
																(episode.diagnosis ||
																	episode.injuryType ||
																	"Infortunio") +
																" · inizio " +
																moment(episode.startDate).format("DD/MM/YYYY") +
																" · Fine: " +
																(episode.endDate
																	? moment(episode.endDate).format("DD/MM/YYYY")
																	: "non indicata") +
																" · Dimissione sportiva: " +
																(episode.sportsDischargeDate
																	? moment(episode.sportsDischargeDate).format(
																			"DD/MM/YYYY",
																		)
																	: "non indicata"),
														)
														.join("; ")
												: full?.medicalPlayerIds?.has(id)
													? "Senza infortunio attivo in cartella"
													: "Nessuna cartella consultata"}
										</b>
									</div>

									<div className="ds-facts__row">
										<span>Recupero · sforzo (7 giorni)</span>
										<b>
											{wellness
												? `${fmt(wellness?.avgTQR, 1)} su 20 (${wellness?.tqr?.measurements ?? "—"} rilevazioni TQR) · ${fmt(wellness?.avgRPE, 1)} su 10 (${wellness?.rpe?.measurements ?? "—"} rilevazioni RPE) · ${team?.name}`
												: "Dati non disponibili"}
										</b>
									</div>
									<div className="ds-facts__row">
										<span>Assenze agli allenamenti (14 giorni)</span>
										<b>
											{trainingAttendance
												? trainingAttendance.absent +
													" assenti · " +
													trainingAttendance.injured +
													" infortunati · " +
													trainingAttendance.unknown +
													" da verificare su " +
													trainingAttendance.total +
													" sedute attese · " +
													team.name
												: "Dati non disponibili"}
										</b>
									</div>
								</div>
							</DsSection>

							<DsSection
								title="Contratto e carriera"
								action={<DsLink to="/ds/mercato">Mercato</DsLink>}
							>
								<div className="ds-facts">
									{!detail.contractsAvailable ? (
										<p role="status">
											Contratti non disponibili o accesso non consentito.
										</p>
									) : contracts.length ? (
										contracts.map((c) => (
											<div className="ds-facts__row" key={c.id}>
												<span>
													{CONTRACT_TYPES[c.contractType] ||
														c.contractType ||
														"Contratto"}{" "}
													·{" "}
													{CONTRACT_STATUS[c.status] ||
														c.status ||
														(c.isActive
															? "Attivo secondo il contratto"
															: "Stato non indicato")}
												</span>
												<b>
													{c.startDate
														? moment(c.startDate).format("D MMM YYYY")
														: "—"}{" "}
													–{" "}
													{c.endDate
														? moment(c.endDate).format("D MMM YYYY")
														: "—"}
													<br />
													{formatEuro(c.totalMonthlySalary)} al mese
												</b>
											</div>
										))
									) : (
										<div className="ds-facts__row">
											<span>Contratto</span>
											<b>Nessun contratto</b>
										</div>
									)}

									<div className="ds-facts__row">
										<span>Procuratore</span>
										<b>
											{agentName
												? `${agentName}${agent.dateFrom ? " · dal " + moment(agent.dateFrom).format("MMM YYYY") : ""}`
												: "Nessuno"}
										</b>
									</div>
									{career.map((c, i) => (
										<div key={i} className="ds-facts__row">
											<span>{c.season?.name || "Stagione"}</span>
											<b>
												{[c.club?.name, c.playerCategory?.name]
													.filter(Boolean)
													.join(" · ") || "—"}
											</b>
										</div>
									))}
								</div>
							</DsSection>

							<DsSection title="Fisico e test">
								<div className="ds-facts">
									<div className="ds-facts__row">
										<span>Altezza · peso</span>
										<b>
											{player.lastHeight || player.height
												? `${fmt(player.lastHeight || player.height)} cm`
												: "—"}{" "}
											·{" "}
											{player.lastWeight || player.weight
												? `${fmt(player.lastWeight || player.weight, 1)} kg`
												: "—"}
										</b>
									</div>
									{growthRate !== null && (
										<div className="ds-facts__row">
											<span>Crescita</span>
											<b>{fmt(growthRate, 1)} cm l'anno</b>
										</div>
									)}
									<div className="ds-facts__row">
										<span>Ultimo test atletico</span>
										<b>
											{lastTest
												? `${lastTest.testType || lastTest.description || "Test"} · ${moment(lastTest.testDate).format("D MMM YYYY")}`
												: "Nessuno"}
										</b>
									</div>
								</div>
							</DsSection>

							<div className="ds-player__gps">
								<DsSection
									title="GPS delle partite"
									hint="Sintesi della stagione selezionata. Sessioni KSport escluse."
									action={
										<button
											className="ds-link"
											onClick={() => {
												dispatch(setPlayerTab("performance"));
												navigate("/players/" + id);
											}}
										>
											Dettaglio GPS nella vista completa
										</button>
									}
								>
									<DsPlayerGps
										key={id + seasonId}
										playerId={id}
										seasonId={seasonId}
									/>
								</DsSection>
							</div>

							<div className="ds-player__evaluations">
								{Array.isArray(full?.scoutingReports) && (
									<DsPlayerScouting
										key={id + seasonId}
										playerId={id}
										snapshot={full}
									/>
								)}
								<DsSection title="Valutazioni e report storici">
									<div className="ds-facts">
										<div className="ds-facts__row">
											<span>Valutazioni dello staff</span>
											<b>
												{valuations.length
													? `${valuations.length} · ultima il ${moment(valuations[0].valuationDate || valuations[0].creationDate).format("D MMM")}${valuations[0].valuationAuthor ? " di " + valuations[0].valuationAuthor : ""}`
													: "Nessuna"}
											</b>
										</div>
										<div className="ds-facts__row">
											<span>Report scouting storici</span>
											<b>
												{reports.length
													? `${reports.length} · ultimo il ${moment(reports[0].reportDate || reports[0].creationDate).format("D MMM")}${reports[0].scout ? " di " + reports[0].scout : ""}`
													: "Nessuno"}
											</b>
										</div>
									</div>
									{notes.slice(0, 2).map((n, i) => (
										<p key={n.id || i} className="ds-quote">
											{noteText(n)}
										</p>
									))}
								</DsSection>
							</div>
						</div>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsPlayerPage;
